import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: Outbound Logistics Invoice PRINT reports (rpCtrlSalesInvCustoms /
// rpCtrlSalesInvCustomer[_KNQ] / rpCtrlSalesPKL*). One reusable PRESENTATIONAL
// dialog for Customs / Customer / PKL — the HTML arrives via props (the Frame
// owns which preview query to call). Mirrors DoPrintDialog: iframe srcDoc
// preview + Print button (hidden when !canEdit) → printAndUploadReport,
// responsive A4 fit (skill /responsive-style + /convert-pr).

interface InvoicePrintDialogProps {
  open: boolean
  /** Dialog title (e.g. "Print Preview - Customer (INV3789.F)"). */
  title: string
  /** HTML preview (parent-fetched). */
  html: string
  /** Fetching HTML (overlay spinner). */
  loading: boolean
  /** Tiền tố tên file PDF (Contract Ref) → `<prefix>_<id>.pdf`. */
  fileNamePrefix?: string
  /** View-only accounts: hide the real Print button (still see the preview). */
  canEdit?: boolean
  onClose: () => void
  /** Nếu có → hiện nút "Back" (vd PKL: quay lại form chooser Pr-PKL). */
  onBack?: () => void
}

export default function InvoicePrintDialog({
  open,
  title,
  html,
  loading,
  canEdit = true,
  fileNamePrefix,
  onClose,
  onBack,
}: InvoicePrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const iframeTitle = 'Invoice Print Preview'
  const previewHtml = html ? buildPrintPreviewHtml(html, { styleId: 'inv-print-preview-overrides' }) : ''

  return (
    <>
      {open && loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title.replace(/^Print Preview - /, '')}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onBack && (
              <Button onClick={onBack} size="small">
                Back
              </Button>
            )}
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const idPart = title.replace(/^.*\(([^)]+)\).*$/, '$1')
                  // Tên file PDF = <Contract Ref>_<id>.pdf (bỏ prefix nếu ref rỗng).
                  const prefix = sanitizePdfFileNamePart(fileNamePrefix || '')
                  const idName = sanitizePdfFileNamePart(idPart || 'sale_invoice')
                  const printTitle = `${prefix ? `${prefix}_` : ''}${idName}.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesInvoice',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
